import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  #fetchProductPageHandler = () => {
    if (!this.refs.quickAdd?.cachedProductHtml) {
      this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    link.addEventListener('touchstart', this.#handleMobileTouchStart, { capture: true, passive: true });
    link.addEventListener('touchend', this.#handleMobileImageTap, { capture: true, passive: false });
    link.addEventListener('click', this.#handleMobileImageTap, { capture: true });

    if (this.dataset.productVariantsSize === '1') return;

    link.addEventListener('click', this.navigateToProduct);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });

    // On mobile PLP: slideshow applies 2-slide limit via data-mobile-slide-limit
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    const link = this.refs.productCardLink;
    link.removeEventListener('touchstart', this.#handleMobileTouchStart, { capture: true });
    link.removeEventListener('touchend', this.#handleMobileImageTap, { capture: true });
    link.removeEventListener('click', this.#handleMobileImageTap, { capture: true });
    link.removeEventListener('click', this.navigateToProduct);
    this.#clearMobileTapResetTimeout();
  }

  /** On desktop resize, restore variant-image visibility and clear PLP tap state */
  #handleMediaQueryChange = () => {
    if (mediaQueryLarge.matches) {
      delete this.dataset.plpLastTap;
      this.refs.cardGallery?.removeAttribute('data-plp-last-tap');
      this.refs.cardGallery?.removeAttribute('data-plp-ignore-sync');
      if (this.variantPicker) this.#updateVariantImages();
    }
  };

  /** Force-hide slides 2+ on mobile right before tap handling; ensures limit is applied at tap time */
  #applyMobileSlideLimitAtTap(/** @type {import('slideshow').Slideshow} */ slideshow) {
    if (mediaQueryLarge.matches) return;
    const slides =
      slideshow.refs.slides?.length
        ? slideshow.refs.slides
        : Array.from(slideshow.refs.scroller?.querySelectorAll?.(':scope > slideshow-slide') ?? []);
    slides.forEach((slide, i) => {
      if (i >= 2) slide.setAttribute('hidden', '');
    });
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;
  };

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      this.refs.productCardLink.href = anchorElement.href;
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #mobileTapResetTimeout = undefined;

  /** True when we handled a touch; used to ignore the delayed click that may still fire on iOS */
  #touchHandled = false;

  /** Aborts the pointer blocker when second-tap window expires */
  /** @type {AbortController | null} */
  #pointerBlockerController = null;

  /** True when navigating from second tap; keeps blocker active to catch synthesized mousedown */
  #navigatingFromSecondTap = false;

  static #MOBILE_TAP_RESET_MS = 3000;

  /**
   * Registers the pointer blocker on touchstart so it is active before the second tap's
   * pointerdown (which fires before touchend).
   * @param {TouchEvent} event
   */
  #handleMobileTouchStart = (event) => {
    if (mediaQueryLarge.matches) return;
    const cardGallery = this.refs.cardGallery;
    const { slideshow } = this.refs;
    const slideshowEl = cardGallery?.querySelector('slideshow-component');
    const inSlideshow = event.target instanceof Element && slideshowEl?.contains(event.target);
    if (!cardGallery || !slideshow?.slides || slideshow.slides.length <= 1) return;
    if (!(event.target instanceof Element) || !inSlideshow) return;
    if (event.target.closest('button, [role="button"], input, select, textarea')) return;
    // Block scroll sync from first touch so slide index cannot revert before second tap
    cardGallery.setAttribute('data-plp-ignore-sync', '');
    this.#applyMobileSlideLimitAtTap(slideshow);
    this.#blockSlideshowPointerEvents();
  };

  /**
   * On mobile: first tap on the card-gallery image advances to next slide;
   * second tap navigates to the product page.
   * @param {MouseEvent} event
   */
  #handleMobileImageTap = async (event) => {
    const isTouch = event.type === 'touchend';

    if (!isTouch && this.#touchHandled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (mediaQueryLarge.matches) return;
    if (event.defaultPrevented || event.target instanceof HTMLInputElement) return;
    if (event.target instanceof Element && event.target.closest('button, [role="button"], input, select, textarea')) return;

    const cardGallery = this.refs.cardGallery;
    const { slideshow } = this.refs;
    const slideshowEl = cardGallery?.querySelector('slideshow-component');
    const inSlideshow = event.target instanceof Element && slideshowEl?.contains(event.target);

    if (!cardGallery || !slideshow?.slides || slideshow.slides.length <= 1) return;
    if (!(event.target instanceof Element) || !inSlideshow) return;

    this.#applyMobileSlideLimitAtTap(slideshow);

    const now = Date.now();
    const lastTap = this.dataset.plpLastTap ?? cardGallery?.getAttribute('data-plp-last-tap');
    const lastTapTime = lastTap ? parseInt(String(lastTap), 10) : null;
    const isSecondTap =
      lastTapTime != null && !isNaN(lastTapTime) && now - lastTapTime < ProductCard.#MOBILE_TAP_RESET_MS;

    if (isSecondTap) {
      const url = this.refs.productCardLink.href;
      event.preventDefault();
      event.stopPropagation();
      if (isTouch) this.#touchHandled = true;
      delete this.dataset.plpLastTap;
      cardGallery?.removeAttribute('data-plp-last-tap');
      cardGallery?.removeAttribute('data-plp-ignore-sync');
      this.#clearMobileTapResetTimeout();
      if (url) {
        this.#navigatingFromSecondTap = true;
        window.location.href = url;
      }
      return;
    }

    event.preventDefault();
    if (isTouch) {
      this.#touchHandled = true;
      setTimeout(() => { this.#touchHandled = false; }, 400);
      this.#blockSlideshowPointerEvents();
    }
    this.dataset.plpLastTap = String(now);
    cardGallery?.setAttribute('data-plp-last-tap', String(now));
    cardGallery?.setAttribute('data-plp-ignore-sync', '');
    this.#setSlideshowPointerBlock(slideshow, true);
    this.#scheduleMobileTapReset();

    const rawNext = slideshow.nextIndex;
    const lastIdx = (slideshow.slides?.length ?? 1) - 1;
    const nextIdx = rawNext > lastIdx ? lastIdx : rawNext;
    await slideshow.select(nextIdx, event, { animate: true });
    setTimeout(() => this.#preloadNextPreviewImage());
  };

  /**
   * Blocks slideshow pointer events during second-tap window. Registered on first tap
   * because pointerdown fires before touchend. Also blocks synthesized mousedown after touchend.
   */
  #blockSlideshowPointerEvents() {
    this.#pointerBlockerController?.abort();
    const controller = new AbortController();
    this.#pointerBlockerController = controller;
    const { signal } = controller;
    const cardGallery = this.refs.cardGallery;
    const block = (/** @type {Event} */ e) => {
      if (signal.aborted) return;
      const targetInCard =
        (e.target instanceof Node && this.contains(e.target)) ||
        (e.composedPath && e.composedPath().includes(this));
      const inSecondTapWindow = !!this.dataset.plpLastTap || cardGallery?.hasAttribute('data-plp-last-tap');
      const shouldBlock =
        (targetInCard && inSecondTapWindow) || (targetInCard && this.#navigatingFromSecondTap);
      if (shouldBlock) {
        e.stopPropagation();
        e.preventDefault();
      }
      // Only abort when not blocking - keep listener to block synthesized mousedown after touchend
      if ((e.type === 'pointerup' || e.type === 'mouseup') && !shouldBlock) {
        controller.abort();
      }
    };
    document.addEventListener('pointerdown', block, { capture: true, signal });
    document.addEventListener('pointerup', block, { capture: true, signal });
    document.addEventListener('mousedown', block, { capture: true, signal });
    document.addEventListener('mouseup', block, { capture: true, signal });
  }

  #clearMobileTapResetTimeout() {
    if (this.#mobileTapResetTimeout) {
      clearTimeout(this.#mobileTapResetTimeout);
      this.#mobileTapResetTimeout = undefined;
    }
  }

  #scheduleMobileTapReset() {
    this.#clearMobileTapResetTimeout();
    this.#mobileTapResetTimeout = setTimeout(() => {
      delete this.dataset.plpLastTap;
      this.refs.cardGallery?.removeAttribute('data-plp-last-tap');
      // Do NOT remove data-plp-ignore-sync here: clearing it allows scroll sync to run
      // and revert the slide before a late second tap. Only remove on navigate or desktop.
      this.#setSlideshowPointerBlock(this.refs.slideshow, false);
      this.#pointerBlockerController?.abort();
      this.#pointerBlockerController = null;
      this.#mobileTapResetTimeout = undefined;
    }, ProductCard.#MOBILE_TAP_RESET_MS);
  }

  /** Block slideshow from receiving pointer events during second-tap window */
  #setSlideshowPointerBlock(/** @type {import('slideshow').Slideshow | undefined} */ slideshow, /** @type {boolean} */ block) {
    const scroller = slideshow?.refs?.scroller;
    if (scroller) {
      scroller.style.pointerEvents = block ? 'none' : '';
    }
  }

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    const { slideshow } = this.refs;

    if (!slideshow || event.pointerType !== 'mouse') return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   */
  resetImage() {
    // Don't reset during PLP second-tap window - pointerleave fires when finger lifts after first tap
    if (this.dataset.plpLastTap || this.refs.cardGallery?.hasAttribute('data-plp-last-tap')) return;
    const { slideshow } = this.refs;
    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    const defaultSlide = slideshow.defaultSlide;
    const slideId = defaultSlide?.getAttribute('slide-id');
    if (defaultSlide && slideshow.slides?.includes(defaultSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    } else if (!this.variantPicker?.selectedOption) {
      slideshow.previous(undefined, { animate: false });
      return;
    }

    const id = this.variantPicker.selectedOption.dataset.optionMediaId;
    if (!id) {
      slideshow.previous(undefined, { animate: false });
      return;
    }

    slideshow.select({ id }, undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof HTMLAnchorElement)) return;

    const productCardAnchor = event.target.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = event.target.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }
    history.replaceState({}, '', url.toString());
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 *
 * @typedef {object} SwatchesRefs
 * @property {HTMLElement} overflowList
 *
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
